import { useState, type ReactNode } from 'react';
import type { Page } from '@connaro/shared';
import { ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner } from './states';
import { EmptyState, ErrorState, Loading } from './states';

export interface Column<T> {
  header: string;
  render: (row: T) => ReactNode;
}

export interface FieldDef {
  name: string;
  label: string;
  type?: 'text' | 'select';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export interface CreateConfig {
  label: string;
  fields: FieldDef[];
  submit: (values: Record<string, string>) => Promise<void>;
}

interface ResourceListProps<T extends { id: string }> {
  title: string;
  load: () => Promise<Page<T>>;
  columns: Array<Column<T>>;
  loadKeys?: ReadonlyArray<unknown>;
  create?: CreateConfig;
  emptyHint?: string;
  rowActions?: (row: T, reload: () => void) => ReactNode;
}

export function ResourceList<T extends { id: string }>({
  title,
  load,
  columns,
  loadKeys = [],
  create,
  emptyHint,
  rowActions,
}: ResourceListProps<T>): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(load, loadKeys);
  const [showForm, setShowForm] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!create) return;
    setSubmitting(true);
    setSubmitError(null);
    setSuccess(false);
    try {
      await create.submit(values);
      setValues({});
      setShowForm(false);
      setSuccess(true);
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section>
      <div className="page-head">
        <h1>{title}</h1>
        {create ? (
          <button type="button" className="btn" onClick={() => setShowForm((s) => !s)}>
            {showForm ? t('common.cancel') : create.label}
          </button>
        ) : null}
      </div>

      {success ? <Banner variant="success">{t('states.success')}</Banner> : null}

      {create && showForm ? (
        <form className="card form" onSubmit={(e) => void onSubmit(e)}>
          {submitError ? <Banner variant="error">{submitError}</Banner> : null}
          {create.fields.map((field) => (
            <label key={field.name} className="field">
              <span>{field.label}</span>
              {field.type === 'select' ? (
                <select
                  required={field.required}
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
                >
                  <option value="">—</option>
                  {(field.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  required={field.required}
                  placeholder={field.placeholder}
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
                />
              )}
            </label>
          ))}
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? t('states.loading') : t('common.save')}
          </button>
        </form>
      ) : null}

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error.message} onRetry={reload} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState hint={emptyHint} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.header}>{c.header}</th>
                ))}
                {rowActions ? <th aria-label="actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.id}>
                  {columns.map((c) => (
                    <td key={c.header}>{c.render(row)}</td>
                  ))}
                  {rowActions ? <td className="row-actions">{rowActions(row, reload)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
